<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import InboxMembersAPI from '../../../../api/inboxMembers';
import NextButton from 'dashboard/components-next/button/Button.vue';
import router from '../../../index';
import PageHeader from '../SettingsSubPageHeader.vue';
import multiselect from 'vue-multiselect';

export default {
  components: {
    PageHeader,
    NextButton,
    multiselect,
  },
  data() {
    return {
      isCreating: false,
      selectedAgents: [],
      isLoading: true,
      voiceConfig: null,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      currentInbox: 'inboxes/getInbox',
    }),
    inboxId() {
      return this.$route.params.inbox_id;
    },
    inbox() {
      return this.currentInbox(this.inboxId);
    },
    isVoiceChannel() {
      return this.inbox?.channel_type === 'Channel::Voice';
    },
    // Informação de canais contratados (apenas visual/informativo)
    totalChannels() {
      return this.voiceConfig?.total_channels || 0;
    },
    hasSelectedAgents() {
      return this.selectedAgents.length > 0;
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchCurrentConfig();
  },
  methods: {
    async fetchCurrentConfig() {
      if (!this.inboxId) return;

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inboxId,
        });

        const { data } = response;

        // Armazenar voice_config (contém total_channels)
        this.voiceConfig = data.data?.voice_config || null;

        // Carregar agentes já selecionados
        const currentAgentIds = data.data?.agent_ids || [];
        this.selectedAgents = this.agentList.filter(agent =>
          currentAgentIds.includes(agent.id)
        );
      } catch (error) {
        console.error('Erro ao carregar configuração:', error);
        useAlert('Erro ao carregar configurações do canal');
      } finally {
        this.isLoading = false;
      }
    },

    async addAgents() {
      if (!this.hasSelectedAgents) {
        useAlert('Selecione pelo menos um agente');
        return;
      }

      this.isCreating = true;

      try {
        const payload = {
          inboxId: this.inboxId,
          agentIds: this.selectedAgents.map(a => a.id),
        };

        await InboxMembersAPI.update(payload);

        router.replace({
          name: 'settings_inbox_finish',
          params: {
            page: 'new',
            inbox_id: this.inboxId,
          },
        });
      } catch (error) {
        console.error('Erro ao salvar:', error);
        const errorMessage = error.response?.data?.error || error.message;
        useAlert(errorMessage);
      } finally {
        this.isCreating = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="addAgents()">
      <div class="w-full">
        <PageHeader
          :header-title="$t('INBOX_MGMT.ADD.VOICE.AGENTS.TITLE')"
          :header-content="$t('INBOX_MGMT.ADD.VOICE.AGENTS.DESC')"
        />
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <span class="text-n-slate-11">Carregando...</span>
      </div>

      <div v-else>
        <!-- Info de Canais Contratados (apenas informativo) -->
        <div
          v-if="voiceConfig && totalChannels > 0"
          class="mb-4 p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <div class="flex items-center gap-2 text-sm text-n-slate-11">
            <svg 
              class="w-4 h-4 text-n-slate-11" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>
              Este canal possui <strong class="text-n-slate-12">{{ totalChannels }} canais simultâneos</strong> contratados.
            </span>
          </div>
          <p class="mt-1 text-xs text-n-slate-10 ml-6">
            Você pode adicionar quantos agentes quiser. Os canais determinam apenas quantas ligações podem ocorrer ao mesmo tempo.
          </p>
        </div>

        <!-- Seleção de Agentes -->
        <div class="mb-4">
          <label class="block">
            <span class="text-sm font-medium text-n-slate-12 mb-2 block">
              {{ $t('INBOX_MGMT.ADD.VOICE.AGENTS.SELECT_LABEL') }}
              <span class="text-n-ruby-9">*</span>
            </span>
            <multiselect
              v-model="selectedAgents"
              :options="agentList"
              track-by="id"
              label="name"
              multiple
              :close-on-select="false"
              :clear-on-select="false"
              hide-selected
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              :placeholder="$t('INBOX_MGMT.ADD.VOICE.AGENTS.SELECT_PLACEHOLDER')"
              class="voice-agents-multiselect"
            >
              <template #option="{ option }">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ option.name }}</span>
                  <span class="text-xs text-n-slate-10">{{ option.email }}</span>
                </div>
              </template>
              <template #tag="{ option, remove }">
                <span class="multiselect__tag">
                  <span>{{ option.name }}</span>
                  <i
                    class="multiselect__tag-icon"
                    @click="remove(option)"
                  />
                </span>
              </template>
            </multiselect>
          </label>

          <!-- Status de Validação -->
          <div class="mt-3 flex items-center gap-2">
            <svg
              :class="hasSelectedAgents ? 'text-n-teal-11' : 'text-n-amber-11'"
              class="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                v-if="hasSelectedAgents"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p
              :class="hasSelectedAgents ? 'text-n-teal-11' : 'text-n-amber-11'"
              class="text-sm font-medium"
            >
              <template v-if="!hasSelectedAgents">
                {{ $t('INBOX_MGMT.ADD.VOICE.AGENTS.VALIDATION_EMPTY') }}
              </template>
              <template v-else>
                {{ selectedAgents.length }} 
                {{ selectedAgents.length === 1 
                  ? $t('INBOX_MGMT.ADD.VOICE.AGENTS.VALIDATION_SINGULAR') 
                  : $t('INBOX_MGMT.ADD.VOICE.AGENTS.VALIDATION_PLURAL') 
                }}
              </template>
            </p>
          </div>
        </div>

        <!-- Explicação sobre endpoints -->
        <div class="mb-4 p-3 bg-n-slate-2 border border-n-slate-6 rounded-lg">
          <div class="flex items-start gap-2">
            <svg 
              class="w-5 h-5 text-n-iris-11 flex-shrink-0 mt-0.5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
              />
            </svg>
            <div class="text-sm text-n-slate-11">
              <p class="font-medium text-n-slate-12 mb-1">
                {{ $t('INBOX_MGMT.ADD.VOICE.AGENTS.INFO_TITLE') }}
              </p>
              <p>
                {{ $t('INBOX_MGMT.ADD.VOICE.AGENTS.INFO_DESC') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Botão de submit -->
        <div class="w-full mt-4">
          <NextButton
            type="submit"
            :is-loading="isCreating"
            :disabled="!hasSelectedAgents"
            :label="$t('INBOX_MGMT.AGENTS.BUTTON_TEXT')"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
:deep(.voice-agents-multiselect) {
  .multiselect__tags {
    @apply bg-n-slate-1 border-n-slate-7 rounded-md min-h-[42px];
  }

  .multiselect__input,
  .multiselect__single {
    @apply bg-n-slate-1 text-n-slate-12;
  }

  .multiselect__placeholder {
    @apply text-n-slate-11;
  }

  .multiselect__content-wrapper {
    @apply bg-n-slate-1 border-n-slate-7 rounded-md;
  }

  .multiselect__option--highlight {
    @apply bg-n-iris-4 text-n-slate-12;
  }

  .multiselect__option--selected {
    @apply bg-n-iris-9 text-white font-medium;
  }

  .multiselect__tag {
    @apply bg-n-iris-9 text-white;
  }

  .multiselect__tag-icon:hover {
    @apply bg-n-iris-10;
  }
}
</style>